import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, CheckCircle, XCircle, AlertTriangle, FileText, 
  Download, Eye, MessageSquare, Clock, Building, Coins, Shield
} from 'lucide-react';
import api from '../../lib/api';
import { DocumentViewer } from '../../components/tokenization/DocumentViewer';
import { ApprovalActions } from '../../components/tokenization/ApprovalActions';

interface AssetForReview {
  id: string;
  name: string;
  description: string;
  category: string;
  totalValue: number;
  unitPrice: number;
  tokenSymbol: string;
  tokenName: string;
  maxSupply: number | null;
  tenant: {
    id: string;
    name: string;
    cnpj: string;
  };
  documents: Array<{
    id: string;
    type: string;
    fileName: string;
    fileUrl: string;
    uploadedAt: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    validationNotes: string | null;
  }>;
  custody: {
    custodianName: string;
    custodianType: string;
    registrationNumber: string;
    verificationStatus: string;
  } | null;
  complianceChecks: Array<{
    rule: string;
    status: 'PASSED' | 'FAILED' | 'PENDING';
    message: string;
  }>;
  submittedAt: string;
  reviewHistory: Array<{
    action: string;
    reviewer: string;
    timestamp: string;
    notes: string;
  }>;
}

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundo',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios',
};

const documentTypeLabels: Record<string, string> = {
  LEGAL_OPINION: 'Parecer Jurídico',
  VALUATION_REPORT: 'Laudo de Avaliação',
  CUSTODY_PROOF: 'Comprovante de Custódia',
  OWNERSHIP_PROOF: 'Comprovante de Propriedade',
  REGULATORY_APPROVAL: 'Aprovação Regulatória',
  FINANCIAL_STATEMENTS: 'Demonstrações Financeiras',
  CONTRACT: 'Contrato',
  OTHER: 'Outro',
};

export function AssetReview() {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | 'request_docs'>('approve');

  const { data: asset, isLoading } = useQuery({
    queryKey: ['asset-review', assetId],
    queryFn: async () => {
      const res = await api.get(`/tokenization/compliance/assets/${assetId}`);
      return res.data as AssetForReview;
    },
    enabled: !!assetId,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleApprovalAction = (action: 'approve' | 'reject' | 'request_docs') => {
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
        <p className="text-surface-400">Ativo não encontrado</p>
      </div>
    );
  }

  const passedChecks = asset.complianceChecks.filter(c => c.status === 'PASSED').length;
  const totalChecks = asset.complianceChecks.length;
  const allChecksPassed = passedChecks === totalChecks;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/compliance/queue')}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{asset.name}</h1>
          <p className="text-surface-400">Análise de Compliance</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleApprovalAction('request_docs')}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Solicitar Docs
          </button>
          <button
            onClick={() => handleApprovalAction('reject')}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <XCircle className="w-4 h-4" />
            Rejeitar
          </button>
          <button
            onClick={() => handleApprovalAction('approve')}
            disabled={!allChecksPassed}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Aprovar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Asset Details */}
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Coins className="w-5 h-5 text-primary-400" />
              Detalhes do Ativo
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-surface-400">Categoria</p>
                <p className="font-medium">{categoryLabels[asset.category] || asset.category}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Valor Total</p>
                <p className="font-medium">{formatCurrency(asset.totalValue)}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Símbolo do Token</p>
                <p className="font-medium">{asset.tokenSymbol}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Preço Unitário</p>
                <p className="font-medium">{formatCurrency(asset.unitPrice)}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Supply Máximo</p>
                <p className="font-medium">{asset.maxSupply ? asset.maxSupply.toLocaleString('pt-BR') : 'Ilimitado'}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">Data de Submissão</p>
                <p className="font-medium">{formatDate(asset.submittedAt)}</p>
              </div>
            </div>
            {asset.description && (
              <div className="mt-4 pt-4 border-t border-surface-800">
                <p className="text-sm text-surface-400 mb-1">Descrição</p>
                <p className="text-sm">{asset.description}</p>
              </div>
            )}
          </div>

          {/* Issuer Info */}
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-primary-400" />
              Emissor
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-surface-400">Razão Social</p>
                <p className="font-medium">{asset.tenant.name}</p>
              </div>
              <div>
                <p className="text-sm text-surface-400">CNPJ</p>
                <p className="font-medium">{asset.tenant.cnpj}</p>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary-400" />
              Documentos ({asset.documents.length})
            </h2>
            <div className="space-y-3">
              {asset.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-surface-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-surface-400" />
                    <div>
                      <p className="font-medium">{documentTypeLabels[doc.type] || doc.type}</p>
                      <p className="text-sm text-surface-400">{doc.fileName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      doc.status === 'APPROVED' ? 'bg-green-500/20 text-green-400' :
                      doc.status === 'REJECTED' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {doc.status === 'APPROVED' ? 'Aprovado' : doc.status === 'REJECTED' ? 'Rejeitado' : 'Pendente'}
                    </span>
                    <button
                      onClick={() => setSelectedDocument(doc.id)}
                      className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                      title="Visualizar"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <a
                      href={doc.fileUrl}
                      download
                      className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custody Info */}
          {asset.custody && (
            <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-400" />
                Custódia
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-surface-400">Custodiante</p>
                  <p className="font-medium">{asset.custody.custodianName}</p>
                </div>
                <div>
                  <p className="text-sm text-surface-400">Tipo</p>
                  <p className="font-medium">{asset.custody.custodianType}</p>
                </div>
                <div>
                  <p className="text-sm text-surface-400">Registro</p>
                  <p className="font-medium">{asset.custody.registrationNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-surface-400">Status de Verificação</p>
                  <p className="font-medium">{asset.custody.verificationStatus}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Compliance Checks */}
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
            <h2 className="text-lg font-semibold mb-4">
              Verificações de Compliance
            </h2>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-surface-400">Progresso</span>
                <span className="text-sm font-medium">{passedChecks}/{totalChecks}</span>
              </div>
              <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${allChecksPassed ? 'bg-green-500' : 'bg-yellow-500'}`}
                  style={{ width: `${(passedChecks / totalChecks) * 100}%` }}
                />
              </div>
            </div>
            <div className="space-y-2">
              {asset.complianceChecks.map((check, index) => (
                <div key={index} className="flex items-start gap-2 p-2 rounded-lg bg-surface-800">
                  {check.status === 'PASSED' ? (
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                  ) : check.status === 'FAILED' ? (
                    <XCircle className="w-4 h-4 text-red-400 mt-0.5" />
                  ) : (
                    <Clock className="w-4 h-4 text-yellow-400 mt-0.5" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{check.rule}</p>
                    <p className="text-xs text-surface-400">{check.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Review History */}
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-6">
            <h2 className="text-lg font-semibold mb-4">Histórico de Análise</h2>
            {asset.reviewHistory.length === 0 ? (
              <p className="text-sm text-surface-400">Primeira análise</p>
            ) : (
              <div className="space-y-3">
                {asset.reviewHistory.map((entry, index) => (
                  <div key={index} className="border-l-2 border-surface-700 pl-3">
                    <p className="text-sm font-medium">{entry.action}</p>
                    <p className="text-xs text-surface-400">{entry.reviewer}</p>
                    <p className="text-xs text-surface-500">{formatDate(entry.timestamp)}</p>
                    {entry.notes && (
                      <p className="text-xs text-surface-400 mt-1">{entry.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <DocumentViewer
          documentId={selectedDocument}
          onClose={() => setSelectedDocument(null)}
        />
      )}

      {/* Approval Actions Modal */}
      {showApprovalModal && (
        <ApprovalActions
          assetId={asset.id}
          action={approvalAction}
          onClose={() => setShowApprovalModal(false)}
          onSuccess={() => {
            setShowApprovalModal(false);
            queryClient.invalidateQueries({ queryKey: ['asset-review', assetId] });
            if (approvalAction !== 'request_docs') {
              navigate('/admin/compliance/queue');
            }
          }}
        />
      )}
    </div>
  );
}

export default AssetReview;
